import { useState, useRef, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { authAPI } from '../lib/api'
import api from '../lib/api'
import { QrCode, Camera, X, AlertCircle, CheckCircle } from 'lucide-react'
import { openQrCamera, createQrDecoder } from '../lib/qrScanEngine'
import { silentLog } from '../lib/errorLogger'

// 역할별 라우팅 함수
const navigateByRole = (navigate, role) => {
  switch (role) {
    case 'developer':
    case 'hq':
      navigate('/mobile/qr-login') // 본사 → QR 스캔 페이지
      break
    case 'maker':
      navigate('/mobile/qr-login') // 제작처 → QR 스캔 페이지
      break
    case 'production':
    case 'plant':
      navigate('/mobile/qr-login') // 생산처 → QR 스캔 페이지
      break
    default:
      navigate('/mobile/qr-login')
  }
}

export default function QRLogin() {
  const navigate = useNavigate()
  const { login } = useAuthStore()
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const decoderRef = useRef(null)      // BarcodeDetector 인스턴스 보유 → 스캐너당 1개
  const scanBusyRef = useRef(false)    // 디코딩이 비동기라 스캔 주기와 겹치면 안 된다

  const [scanning, setScanning] = useState(false)
  const [error, setError] = useState('')
  const [cameraError, setCameraError] = useState('')
  const [stream, setStream] = useState(null)
  const [scanInterval, setScanInterval] = useState(null)
  const [detectedQR, setDetectedQR] = useState(null)
  const [moldList, setMoldList] = useState([]) // DB에서 가져온 금형 목록
  const [loadingMolds, setLoadingMolds] = useState(false)

  // DB에서 금형 목록 가져오기
  useEffect(() => {
    const fetchMolds = async () => {
      setLoadingMolds(true)
      try {
        const response = await api.get('/molds?limit=10')
        if (response.data.success && response.data.data) {
          // data가 배열인 경우와 객체인 경우 모두 처리
          const molds = Array.isArray(response.data.data) 
            ? response.data.data 
            : response.data.data.molds || []
          setMoldList(molds.slice(0, 6)) // 최대 6개만 표시
        }
      } catch (err) {
      } finally {
        setLoadingMolds(false)
      }
    }
    fetchMolds()
  }, [])

  useEffect(() => {
    return () => {
      // Cleanup: 카메라 스트림 및 스캔 인터벌 정리
      if (stream) {
        stream.getTracks().forEach(track => track.stop())
      }
      if (scanInterval) {
        clearInterval(scanInterval)
      }
    }
  }, [stream, scanInterval])

  const startCamera = async () => {
    try {
      setCameraError('')
      setDetectedQR(null)
      // 연속 초점 + 해상도 요청은 qrScanEngine 이 맡는다 (2026-11-04 초점 개선, 모바일 파리티)
      const { stream: mediaStream } = await openQrCamera()
      decoderRef.current = createQrDecoder()
      scanBusyRef.current = false

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream
        setStream(mediaStream)
        setScanning(true)
        
        // 비디오가 재생되면 자동 스캔 시작
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play()
          startAutoScan()
        }
      }
    } catch (err) {
      // 에러 유형별 상세 메시지
      if (err.name === 'NotAllowedError') {
        setCameraError('카메라 권한이 거부되었습니다. 브라우저 설정에서 카메라 권한을 허용해주세요.')
      } else if (err.name === 'NotFoundError') {
        setCameraError('카메라를 찾을 수 없습니다. 카메라가 연결되어 있는지 확인해주세요.')
      } else if (err.name === 'NotReadableError') {
        setCameraError('카메라가 다른 앱에서 사용 중입니다. 다른 앱을 종료 후 다시 시도해주세요.')
      } else if (err.name === 'OverconstrainedError') {
        setCameraError('요청한 카메라 설정을 지원하지 않습니다.')
      } else if (err.name === 'SecurityError') {
        setCameraError('보안 정책으로 카메라 접근이 차단되었습니다. HTTPS 연결이 필요합니다.')
      } else {
        setCameraError('카메라에 접근할 수 없습니다. 권한을 확인해주세요.')
      }
    }
  }

  const startAutoScan = () => {
    const interval = setInterval(() => {
      scanQRCode()
    }, 300) // 300ms마다 스캔
    setScanInterval(interval)
  }

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop())
      setStream(null)
    }
    if (scanInterval) {
      clearInterval(scanInterval)
      setScanInterval(null)
    }
    decoderRef.current = null
    scanBusyRef.current = false
    setScanning(false)
    setDetectedQR(null)
  }

  const scanQRCode = async () => {
    if (!videoRef.current || !canvasRef.current || !decoderRef.current) return
    // 디코딩이 비동기라 300ms 주기와 겹칠 수 있다
    if (scanBusyRef.current) return
    scanBusyRef.current = true
    try {
      const code = await decoderRef.current(videoRef.current, canvasRef.current)
      if (code) {
        setDetectedQR(code)
        // QR 코드 감지 시 자동 로그인 시도
        handleQRDetected(code)
      }
    } catch (e) {
      silentLog('QRLogin.scanQRCode', e)
    } finally {
      scanBusyRef.current = false
    }
  }

  const handleQRDetected = async (qrData) => {
    // 중복 처리 방지
    if (detectedQR === qrData) return
    
    stopCamera()
    await handleManualQRInput(qrData)
  }

  // QR 스캔 처리 - 금형 정보 조회 후 금형 페이지로 이동
  const handleManualQRInput = async (qrValue) => {
    try {
      setError('')
      
      
      // QR 스캔 API 호출 (신규 엔드포인트)
      const response = await api.post('/mobile/qr/scan', { 
        code: qrValue
      })
      
      
      if (response.data.success) {
        const { mold, templates } = response.data.data
        
        // 금형 정보를 localStorage에 저장
        localStorage.setItem('cams_scanned_mold', JSON.stringify({
          mold,
          templates,
          scannedAt: new Date().toISOString()
        }))
        
        // 현재 로그인된 사용자 역할 확인
        const authStore = useAuthStore.getState()
        const userRole = authStore.user?.role || 'production'
        
        
        // 금형 개요 페이지로 이동
        navigate(`/mobile/molds/${mold.id}`, {
          state: { 
            role: userRole,
            mold: {
              ...mold,
              shotRate: mold.maxShots > 0 ? Math.round((mold.shotCounter / mold.maxShots) * 100) : 0
            }
          }
        })
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'QR 스캔에 실패했습니다.'
      setError(errorMsg)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        {/* 헤더 */}
        <div className="text-center">
          <QrCode className="mx-auto h-12 w-12 text-primary-600" />
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            QR 코드 로그인
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            금형 QR 코드를 스캔하여 로그인하세요
          </p>
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <p className="ml-3 text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {cameraError && (
          <div className="rounded-md bg-yellow-50 p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-yellow-400" />
              <p className="ml-3 text-sm text-yellow-800">{cameraError}</p>
            </div>
          </div>
        )}

        {/* 카메라 뷰 */}
        {scanning ? (
          <div className="card">
            <div className="relative bg-black rounded-lg overflow-hidden" style={{ aspectRatio: '4/3' }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="w-full h-full object-cover"
              />
              <canvas ref={canvasRef} className="hidden" />

              {/* 스캔 가이드 */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-64 h-64 border-4 border-white border-dashed rounded-lg opacity-50 animate-pulse"></div>
              </div>

              {/* 스캔 상태 표시 */}
              <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                <div className="bg-black bg-opacity-70 text-white px-4 py-2 rounded-full text-sm flex items-center gap-2">
                  {detectedQR ? (
                    <>
                      <CheckCircle size={16} className="text-green-400" />
                      <span>QR 코드 감지됨!</span>
                    </>
                  ) : (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>QR 코드를 스캔 중...</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              <p className="text-xs text-center text-gray-600">
                QR 코드가 자동으로 감지되면 즉시 로그인됩니다
              </p>
              <button
                onClick={stopCamera}
                className="btn-secondary w-full flex items-center justify-center gap-2"
              >
                <X size={20} />
                취소
              </button>
            </div>
          </div>
        ) : (
          <div className="card space-y-4">
            <button
              onClick={startCamera}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              <Camera size={20} />
              카메라 시작
            </button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">또는</span>
              </div>
            </div>

            <Link to="/login" className="btn-secondary w-full text-center block">
              일반 로그인
            </Link>
          </div>
        )}

        {/* 안내 메시지 */}
        <div className="card bg-blue-50 border border-blue-200">
          <div className="flex items-start gap-3">
            <QrCode className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
            <div className="text-sm">
              <p className="font-medium text-blue-900 mb-1">QR 로그인 안내</p>
              <ul className="text-blue-800 space-y-1 list-disc list-inside text-xs">
                <li>금형에 부착된 QR 코드를 스캔하세요</li>
                <li>QR 코드 스캔 시 자동으로 로그인됩니다</li>
                <li>GPS 위치가 자동으로 기록됩니다</li>
                <li>세션은 8시간 동안 유지됩니다</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 개발 테스트용: 빠른 로그인 (개발 환경에서만 표시) */}
        {import.meta.env.DEV && (
        <div className="card bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-200">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-2xl">🔧</span>
            <p className="font-semibold text-purple-900">개발 테스트용 - 빠른 로그인</p>
          </div>

          <div className="space-y-2 mb-4">
            <button
              onClick={async () => {
                try {
                  const response = await authAPI.login({ username: 'developer', password: 'dev123' });
                  const { token, user } = response.data.data;
                  login(user, token);
                  navigateByRole(navigate, user.user_type || user.role || 'developer');
                } catch (err) {
                  setError('로그인 실패');
                }
              }}
              className="w-full px-4 py-3 bg-white border-2 border-purple-300 rounded-lg hover:bg-purple-50 transition-colors text-left"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-purple-900">금형개발 담당자</div>
                  <div className="text-xs text-purple-600">developer / 본사</div>
                </div>
                <div className="text-2xl">👨‍💼</div>
              </div>
            </button>

            <button
              onClick={async () => {
                try {
                  const response = await authAPI.login({ username: 'maker1', password: 'maker123' });
                  const { token, user } = response.data.data;
                  login(user, token);
                  navigateByRole(navigate, user.user_type || user.role || 'maker');
                } catch (err) {
                  setError('로그인 실패');
                }
              }}
              className="w-full px-4 py-3 bg-white border-2 border-purple-300 rounded-lg hover:bg-purple-50 transition-colors text-left"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-purple-900">제작처 담당자</div>
                  <div className="text-xs text-purple-600">maker1 / A제작소</div>
                </div>
                <div className="text-2xl">🏭</div>
              </div>
            </button>

            <button
              onClick={async () => {
                try {
                  const response = await authAPI.login({ username: 'plant1', password: 'plant123' });
                  const { token, user } = response.data.data;
                  login(user, token);
                  navigateByRole(navigate, user.user_type || user.role || 'plant');
                } catch (err) {
                  setError('로그인 실패');
                }
              }}
              className="w-full px-4 py-3 bg-white border-2 border-purple-300 rounded-lg hover:bg-purple-50 transition-colors text-left"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-purple-900">생산처 담당자</div>
                  <div className="text-xs text-purple-600">plant1 / 생산공장1</div>
                </div>
                <div className="text-2xl">🏭</div>
              </div>
            </button>
          </div>

          <div className="relative mb-3">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-purple-300"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-2 bg-gradient-to-br from-purple-50 to-purple-100 text-purple-600">QR 코드 테스트 (DB 연동)</span>
            </div>
          </div>

          {/* DB에서 가져온 금형 목록 */}
          {loadingMolds ? (
            <div className="text-center py-4 text-purple-600 text-sm">
              금형 목록 로딩 중...
            </div>
          ) : moldList.length > 0 ? (
            <div className="grid grid-cols-2 gap-2 mb-3 max-h-48 overflow-y-auto">
              {moldList.map((mold) => (
                <button
                  key={mold.id}
                  onClick={() => handleManualQRInput(mold.mold_code || mold.moldCode)}
                  className="text-left px-3 py-2 bg-white border border-purple-300 rounded hover:bg-purple-50 transition-colors"
                >
                  <div className="text-xs font-semibold text-purple-900 truncate">
                    {mold.mold_name || mold.moldName || mold.mold_code || mold.moldCode}
                  </div>
                  <div className="text-xs text-purple-600">{mold.mold_code || mold.moldCode}</div>
                  <div className="text-xs text-gray-500">{mold.status || 'active'}</div>
                </button>
              ))}
            </div>
          ) : (
            <div className="text-center py-2 text-gray-500 text-xs mb-3">
              등록된 금형이 없습니다
            </div>
          )}

          <div className="flex gap-2">
            <input
              type="text"
              placeholder="QR 코드 값 직접 입력 (예: M2024-001)"
              className="input text-sm flex-1"
              onKeyPress={(e) => {
                if (e.key === 'Enter' && e.target.value) {
                  handleManualQRInput(e.target.value)
                }
              }}
            />
          </div>
        </div>
        )}
      </div>
    </div>
  )
}
